export type DbMariaUser = {
  uid?: string;
  id?: string;
  email?: string;
  role?: string;
  name?: string;
  brand?: string;
  producerId?: string;
  [key: string]: any;
};

const API_BASE = String(
  process.env.REACT_APP_DBMARIA_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://fizmoo.com"
).replace(/\/+$/, "");

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  const hasBody = typeof init.body === "string" && init.body.length > 0;
  if (hasBody && !headers.has("Content-Type") && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers
  });

  const text = await response.text();
  let payload: any = null;

  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    const message =
      (payload && typeof payload === "object" && (payload.error || payload.message)) ||
      `Request failed with status ${response.status}`;
    throw new Error(String(message || "Request failed"));
  }

  return payload as T;
}

export async function loginWithEmailPassword(email: string, password: string): Promise<DbMariaUser> {
  const payload = await request<{ user?: DbMariaUser; data?: DbMariaUser; token?: string; message?: string; error?: string }>(
    "/api/auth/login",
    {
      method: "POST",
      body: JSON.stringify({ email, password })
    }
  );

  const user = payload?.user || payload?.data || payload;
  if (!user || typeof user !== "object") {
    throw new Error(payload?.message || payload?.error || "Login failed");
  }

  if (payload?.token) {
    try {
      localStorage.setItem("dbmaria_auth_token", payload.token);
    } catch {
      // Ignore browser storage errors.
    }
  }

  return user as DbMariaUser;
}

export async function fetchCurrentUser(): Promise<DbMariaUser | null> {
  const payload = await request<{ user?: DbMariaUser; data?: DbMariaUser; error?: string }>("/api/auth/me", {
    method: "GET"
  });

  const user = payload?.user || payload?.data || payload;
  if (!user || typeof user !== "object") {
    return null;
  }

  return user as DbMariaUser;
}

export async function logoutFromServer(): Promise<void> {
  try {
    await request<{ ok?: boolean }>("/api/auth/logout", { method: "POST" });
  } finally {
    try {
      localStorage.removeItem("dbmaria_auth_token");
    } catch {
      // Ignore browser storage errors.
    }
  }
}

export async function registerProducer(params: {
  email: string;
  password: string;
  brand: string;
  producerId: string;
}): Promise<DbMariaUser> {
  const payload = await request<{ user?: DbMariaUser; data?: DbMariaUser; message?: string; error?: string }>(
    "/api/auth/register",
    {
      method: "POST",
      body: JSON.stringify({
        ...params,
        role: "producer"
      })
    }
  );

  const user = payload?.user || payload?.data || payload;
  if (!user || typeof user !== "object") {
    throw new Error(payload?.message || payload?.error || "Registration failed");
  }

  return user as DbMariaUser;
}

export function getApiBaseUrl(): string {
  return API_BASE;
}
